import * as React from 'react'
import * as PropertiesPanel from '../../properties-panel'
import { AgentBlock, Block } from '@basehub/data/replicache/index'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { invariant } from '@epic-web/invariant'
import { useTabsStore } from '../../tabs/store'
import { DocumentSidebarPortal } from '../../document-sidebar-portal'
import {
  ClaudeIcon,
  DeepSeekIcon,
  GeminiIcon,
  OpenAiIcon,
  XaIIcon,
} from '~/components/icons/ai'
import {
  ResetIcon,
  SymbolIcon,
  ExclamationTriangleIcon,
} from '@radix-ui/react-icons'
import {
  productionAssetsURL,
  productionR2Origin,
} from '@basehub/shared-constants'
import { useEditorMode } from '../../editor-mode-provider'
import { Button } from '~/sotano-kit/primitives/button'
import {
  FREE_MODELS,
  PREMIUM_MODELS,
  Repository,
  validateModel,
  mergeWithFreeModels,
} from '@basehub/data/replicache/schema/repository'

import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'

export const getIconForModel = (openrouterId: string) => {
  const company = openrouterId.split('/')[0]
  switch (company) {
    case 'google':
      return <GeminiIcon />
    case 'openai':
      return <OpenAiIcon />
    case 'anthropic':
      return <ClaudeIcon />
    case 'x-ai':
      return <XaIIcon className="size-3" />
    case 'moonshotai':
      return <div>K</div>
    case 'deepseek':
      return <DeepSeekIcon />
    default:
      return null
  }
}

const ModelValidationWarning = ({
  currentModel,
  repository,
}: {
  currentModel: string
  repository: Repository
}) => {
  const validation = validateModel(currentModel, repository)
  const isInvalid = validation.state === 'fallback'
  const addTab = useTabsStore((s) => s.addTab)

  if (!isInvalid) return null

  return (
    <div className="flex gap-2 px-3 py-2 mt-2 bg-orange-50 border rounded-lg bg-dark-error/10 text-dark-error border-dark-error/30 text-xs">
      <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span className="text-xs text-pretty">
        OpenRouter key is required to use this model. Add it&nbsp;
        <button
          className="underline hover:opacity-75"
          onClick={(e) => {
            e.preventDefault()
            addTab({
              key: 'agents:settings',
              partiallyOpen: false,
            })
          }}
        >
          here
        </button>
        .
      </span>
    </div>
  )
}

const accentOptions = [
  {
    id: 'tomato',
    value: '#D13415',
  },
  {
    id: 'red',
    value: '#CE2C31',
  },
  {
    id: 'ruby',
    value: '#CA244D',
  },
  {
    id: 'crimson',
    value: '#CB1D63',
  },
  {
    id: 'pink',
    value: '#C2298A',
  },
  {
    id: 'plum',
    value: '#953EA3',
  },
  {
    id: 'purple',
    value: '#8145B5',
  },
  {
    id: 'violet',
    value: '#6550B9',
  },
  {
    id: 'iris',
    value: '#5753C6',
  },
  {
    id: 'indigo',
    value: '#3A5BC7',
  },
  {
    id: 'blue',
    value: '#0D74CE',
  },
  {
    id: 'cyan',
    value: '#107D98',
  },
  {
    id: 'teal',
    value: '#008573',
  },
  {
    id: 'jade',
    value: '#208368',
  },
  {
    id: 'green',
    value: '#218358',
  },
  {
    id: 'grass',
    value: '#2A7E3B',
  },
  {
    id: 'bronze',
    value: '#7D5E54',
  },
  {
    id: 'gold',
    value: '#71624B',
  },
  {
    id: 'brown',
    value: '#815E46',
  },
  {
    id: 'orange',
    value: '#CC4E00',
  },
  {
    id: 'amber',
    value: '#AB6400',
  },
  {
    id: 'yellow',
    value: '#9E6C00',
  },
  {
    id: 'lime',
    value: '#5C7C2F',
  },
  {
    id: 'mint',
    value: '#027864',
  },
  {
    id: 'sky',
    value: '#00749E',
  },
]

const grayscaleOptions = [
  {
    id: 'gray',
    value: '#646464',
  },
  {
    id: 'mauve',
    value: '#65636D',
  },
  {
    id: 'slate',
    value: '#60646C',
  },
  {
    id: 'sage',
    value: '#5F6563',
  },
  {
    id: 'sand',
    value: '#63635E',
  },
]

export const AgentPropertiesPanel = ({
  block,
  path,
}: {
  block: AgentBlock
  parent?: Block
  path: string
}) => {
  const repoReplicache = useRepoReplicache()
  const replicache = repoReplicache.data?.replicache
  const repository = repoReplicache.data?.repo
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const addTab = useTabsStore((s) => s.addTab)
  const sourceBlockId = block?.sourceBlockId ?? block?.id
  const editorMode = useEditorMode()

  const getInputElement = React.useCallback(() => {
    const avatarInput = document.querySelector<HTMLInputElement>(
      `input#agent-avatar-input-${idToSourceBlockId(block)}`
    )

    if (avatarInput) return avatarInput
  }, [block])

  const getInputFocusOp = React.useCallback(
    (id: string | undefined) => {
      invariant(block?.id, 'Agent ID is required to get input focus operation')
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block?.id, block?.sourceBlockId, activeTabKey]
  )
  return (
    <PropertiesPanel.PropertiesContainer>
      <PropertiesPanel.Group
        title={
          <span className="flex items-center gap-x-1 justify-between w-full">
            <span>Model</span>
            <span>
              <Button
                unstyled
                focusStyled
                className="text-xs font-medium leading-normal text-dark-control-base hover:text-dark-control-highlight cursor-default"
                type="button"
                onClick={() => {
                  addTab({
                    key: 'agents:settings',
                    partiallyOpen: false,
                  })
                }}
              >
                Configure
              </Button>
            </span>
          </span>
        }
      >
        <PropertiesPanel.SelectorOption<PropertiesPanel.PropertiesPanelComboboxOption>
          emptyStateLabel="No models available."
          selected={{
            label: block.typeOptions.model,
            value: block.typeOptions.model,
          }}
          id={`el-${sourceBlockId}-pp-model`}
          onSelect={async (value) => {
            // Check if this is a disabled premium model
            if (value.disabled) {
              // Navigate to agents settings instead of selecting
              addTab({
                key: 'agents:settings',
                partiallyOpen: false,
              })
              return
            }

            if (value.value === block.typeOptions.model) return // same value

            const params: UpdateBlockParams = {
              id: block.id,
              data: {
                typeOptions: {
                  model: value.value,
                },
              },
              isoNow: new Date().toISOString(),
              instanceChildTarget: null,
              path,
              strategy: 'merge-deep',
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-model`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-model`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
          renderResult={(search) => {
            if (search) return null

            // Find the current model from all available models
            const hasOpenRouterKey = !!(repository as Repository).agentsConfig
              ?.openRouterApiKey
            const availableModels =
              (repository as Repository).agentsConfig?.availableModels || []

            const mergedModels = mergeWithFreeModels(
              availableModels,
              hasOpenRouterKey
            )
            const currentModel = mergedModels.find(
              (model) => model.openrouterId === block.typeOptions.model
            )

            return (
              <div className="flex items-center gap-1.5 px-1">
                {getIconForModel(block.typeOptions.model) ?? null}
                <span className="font-medium">
                  {currentModel?.label || block.typeOptions.model}
                </span>
              </div>
            )
          }}
          options={() => {
            if (!repository) return []

            const hasOpenRouterKey = !!(repository as Repository).agentsConfig
              ?.openRouterApiKey
            const availableModels =
              (repository as Repository).agentsConfig?.availableModels || []

            // Get all available models using the merge utility
            const mergedModels = mergeWithFreeModels(
              availableModels,
              hasOpenRouterKey
            )

            // Convert to options format
            const availableOptions = mergedModels.map((model) => ({
              label: model.label,
              value: model.openrouterId,
              icon: getIconForModel(model.openrouterId),
              disabled:
                !hasOpenRouterKey &&
                FREE_MODELS.some(
                  (m) => m.openrouterId === model.openrouterId
                ) === false,
            }))

            // If no OpenRouter key, add disabled premium models
            if (!hasOpenRouterKey) {
              const disabledPremiumOptions = PREMIUM_MODELS.map((model) => ({
                label: model.label,
                value: model.openrouterId,
                disabled: true,
                icon: getIconForModel(model.openrouterId),
              }))

              return [...availableOptions, ...disabledPremiumOptions].sort(
                (a, b) => {
                  if (a.disabled && !b.disabled) return 1
                  if (!a.disabled && b.disabled) return -1
                  return 0
                }
              )
            }

            return availableOptions.sort((a, b) => {
              if (a.disabled && !b.disabled) return 1
              if (!a.disabled && b.disabled) return -1
              return 0
            })
          }}
        />

        {repository && (
          <ModelValidationWarning
            currentModel={block.typeOptions.model}
            repository={repository as Repository}
          />
        )}
      </PropertiesPanel.Group>
      {block.typeOptions.avatar && (
        <PropertiesPanel.Group title="Avatar details">
          <PropertiesPanel.Property
            name="URL:"
            value={block.typeOptions.avatar.replace(
              productionR2Origin,
              productionAssetsURL
            )}
          />
          {editorMode !== 'read' && (
            <div className="flex gap-2">
              <Button
                intent="secondary"
                className="grow text-xs"
                type="button"
                onClick={() => {
                  const input = getInputElement()
                  if (input) input.click()
                }}
              >
                <SymbolIcon width={16} height={16} className="mr-1" />
                Replace
              </Button>
              <Button
                type="button"
                id={`el-${sourceBlockId}-pp-remove-media`}
                intent="secondary"
                className="grow text-xs text-dark-error"
                onClick={async () => {
                  const params: UpdateBlockParams = {
                    id: block.id,
                    instanceChildTarget: block.instanceChildTarget,
                    data: {
                      type: 'agent',
                      typeOptions: {
                        avatar: null,
                      },
                    },
                    path,
                    strategy: 'merge-deep',
                    isoNow: new Date().toISOString(),
                  }
                  const result = await replicache?.mutate.updateBlock(params)
                  if (result?.undo) {
                    undoManager.current?.push({
                      redo: [
                        getInputFocusOp(`el-${sourceBlockId}-pp-remove-media`),
                        { op: 'updateBlock', args: params },
                      ],
                      undo: [
                        getInputFocusOp(undefined),
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: result.undo },
                        },
                      ],
                    })
                  }
                }}
              >
                <ResetIcon width={16} height={16} className="mr-1" />
                Revert
              </Button>
            </div>
          )}
        </PropertiesPanel.Group>
      )}
      <PropertiesPanel.Group title="Content tools" spacing="tight">
        <PropertiesPanel.BooleanOption
          name="Read"
          id={`el-${sourceBlockId}-pp-read`}
          value={true} // forced
          disabled
          containerClassName="!mt-0"
          tooltipText="Reading content is always enabled."
          mutate={() => {
            // noop
          }}
        />
        <PropertiesPanel.BooleanOption
          name="Edit"
          id={`el-${sourceBlockId}-pp-edit`}
          value={block.constraints.edit === 'enabled'}
          containerClassName="!mt-0"
          mutate={async (value) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                type: block.type,
                constraints: {
                  edit: value ? 'enabled' : 'disabled',
                },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-edit`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-edit`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.BooleanOption
          name="Search the Web"
          id={`el-${sourceBlockId}-pp-search-the-web`}
          value={block.constraints.searchTheWeb === 'enabled'}
          containerClassName="!mt-0"
          mutate={async (value) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                type: block.type,
                constraints: {
                  searchTheWeb: value ? 'enabled' : 'disabled',
                },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-search-the-web`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-search-the-web`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        {/* <PropertiesPanel.CheckList
          itemName="Access"
          id={`el-${sourceBlockId}-pp-allowed-access`}
          options={[]}
          checkedOptions={[]}
          title="Access"
          mutate={async (mode, options) => {
            const currentAcceptedTools = [...(block.constraints.toolset ?? [])]
            let newAcceptedTools: string[] | null
            if (mode === 'overwrite') {
              newAcceptedTools = options.map((option) => option.id)
            } else {
              newAcceptedTools = mode
                ? currentAcceptedTools.concat(
                    options.map((option) => option.id)
                  )
                : currentAcceptedTools.filter(
                    (t) => !options.some((option) => t === option.id)
                  )
            }

            if (newAcceptedTools.length === BASE_TOOLS.length) {
              newAcceptedTools = null
            }

            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                type: block.type,
                constraints: {
                  toolset: newAcceptedTools,
                },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-allowed-tools`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-allowed-tools`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
          // addNew={{
          //   label: 'New tool',
          //   onClick: async () => {
          //     throw new Error('Not implemented yet')
          //   },
          // }}
        /> */}
      </PropertiesPanel.Group>
      <PropertiesPanel.Group title="Admin Tools" spacing="tight">
        <PropertiesPanel.BooleanOption
          name="Commit"
          id={`el-${sourceBlockId}-pp-commit`}
          value={block.constraints.commit === 'enabled'}
          containerClassName="!mt-0"
          mutate={async (value) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                type: block.type,
                constraints: {
                  commit: value ? 'enabled' : 'disabled',
                },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-commit`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-commit`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.BooleanOption
          name="Get User Info"
          id={`el-${sourceBlockId}-pp-get-user-info`}
          value={block.constraints.getUserInfo === 'enabled'}
          containerClassName="!mt-0"
          mutate={async (value) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                type: block.type,
                constraints: {
                  getUserInfo: value ? 'enabled' : 'disabled',
                },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-get-user-info`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-get-user-info`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.BooleanOption
          name="Manage Branches"
          id={`el-${sourceBlockId}-pp-manage-branches`}
          value={block.constraints.manageBranches === 'enabled'}
          containerClassName="!mt-0"
          mutate={async (value) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                type: block.type,
                constraints: {
                  manageBranches: value ? 'enabled' : 'disabled',
                },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-manage-branches`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-manage-branches`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
      </PropertiesPanel.Group>
      <PropertiesPanel.Group title="Appearance">
        <PropertiesPanel.ColorPickerInput
          id={`el-${sourceBlockId}-pp-accent`}
          options={accentOptions.map((option) => {
            return {
              ...option,
              tooltip: `${option.id[0]!.toUpperCase() + option.id.slice(1)} ${option.value}`,
            }
          })}
          name="Accent"
          value={
            accentOptions.find(
              (option) => option.id === block.typeOptions.accent
            )?.value ||
            accentOptions[0]?.value ||
            ''
          }
          mutate={async (hex) => {
            const value = accentOptions.find(
              (option) => option.value === hex
            )!.id
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                type: block.type,
                typeOptions: { accent: value },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-accent`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-accent`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.ColorPickerInput
          id={`el-${sourceBlockId}-pp-grayscale`}
          options={grayscaleOptions.map((option) => {
            return {
              ...option,
              tooltip: `${option.id[0]!.toUpperCase() + option.id.slice(1)} ${option.value}`,
            }
          })}
          name="Grayscale"
          value={
            grayscaleOptions.find(
              (option) => option.id === block.typeOptions.grayscale
            )?.value ||
            grayscaleOptions[0]?.value ||
            ''
          }
          mutate={async (hex) => {
            const value = grayscaleOptions.find(
              (option) => option.value === hex
            )!.id
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                type: block.type,
                typeOptions: { grayscale: value },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-grayscale`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-grayscale`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
      </PropertiesPanel.Group>
    </PropertiesPanel.PropertiesContainer>
  )
}

export const FullViewPropertiesPanelWrapper = ({
  block,
  path,
  children,
  parentBlock,
}: {
  block: AgentBlock
  path: string
  children: React.ReactNode
  parentBlock: Block | null
}) => {
  return (
    <DocumentSidebarPortal hidden={false}>
      <PropertiesPanel.Root block={block} parentBlock={parentBlock}>
        <PropertiesPanel.BlockTitleGroup block={block} path={path} />
        {children}
      </PropertiesPanel.Root>
    </DocumentSidebarPortal>
  )
}
